import {defineStore} from "pinia";
import axios from "axios";
import AuthUser from "@src/types/AuthUser";
import {destroyEcho, initEcho, setAuthFailureHandler} from "@src/services/echo";
import useStore from "@src/stores/store";

const USER_KEY = "user";
const SESSION_KEY = "session";
const TOKEN_KEY = "dchatToken";
export const USER_DEVICE_ID_KEY = "userDeviceId";

export const useAuthStore = defineStore("auth", {
    state: () => ({
        user: null as AuthUser | null,
        token: null,
        sessionId: 0
    }),

    getters: {
        isAuthenticated: (state) => !!state.user,
    },

    actions: {
        loadFromStorage() {
            const raw = localStorage.getItem(USER_KEY);
            if (!raw) return;

            try {
                const parsed = JSON.parse(raw);
                this.user = parsed?.user ?? null;
            } catch {
                this.user = null;
            }
        },

        saveToStorage() {
            localStorage.setItem(USER_KEY, JSON.stringify({ user: this.user }));
            localStorage.setItem(SESSION_KEY, JSON.stringify(this.sessionId));
        },

        clearStorage() {
            localStorage.removeItem(USER_KEY);
            localStorage.removeItem(SESSION_KEY);
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_DEVICE_ID_KEY);
        },

        async getUser() {
            try {
                const response = await axios.get("/operator/v4/user");
                this.user = response.data.data ?? null;
                this.sessionId = response.data.session.id ?? 0;
                const token = localStorage.getItem('dchatToken');
                const store = useStore();
                store.settings.darkMode = (response.data.data.settings?.theme === 'dark');

                // mantém storage consistente
                if (this.user && token) {
                    initEcho();
                    setAuthFailureHandler(() => {
                        this.user = null;
                        this.clearStorage();
                        useStore().goToLogin();
                    });
                    this.saveToStorage();
                } else {
                    destroyEcho();
                    this.clearStorage();
                }
            } catch {
                this.user = null;
                destroyEcho();
                this.clearStorage();
            }
        },

        async login(credentials: { email: string; password: string }) {
            await axios.get("/sanctum/csrf-cookie");

            const response = await axios.post("/operator/v4/login", credentials);

            // backend retorna 200 mesmo quando falha
            if (response?.data?.success === false) {
                const err: any = new Error("INVALID_CREDENTIALS");
                err.code = "INVALID_CREDENTIALS";
                throw err;
            }

            // await this.getUser();
            this.user = response.data.user ?? null;

            // mantém storage consistente
            if (this.user) {
                localStorage.setItem(TOKEN_KEY, response.data.token);
                initEcho();
                setAuthFailureHandler(() => {
                    this.user = null;
                    this.clearStorage();
                    useStore().goToLogin();
                });
                this.saveToStorage();
            } else {
                destroyEcho();
                this.clearStorage();
            }

            if (!this.user) {
                const err: any = new Error("NOT_AUTHENTICATED");
                err.code = "NOT_AUTHENTICATED";
                throw err;
            }
        },

        async recover(credentials: { email: string }): Promise<{ success: boolean; message: string }> {
            await axios.get("/sanctum/csrf-cookie");

            const response = await axios.post("/operator/v4/recover-password", credentials);

            return response.data;
        },

        async logout() {
            const deviceId = localStorage.getItem(USER_DEVICE_ID_KEY);

            await axios.get("/operator/v4/logout", deviceId
                ? {params: {user_device_id: deviceId}}
                : undefined);

            this.user = null;
            this.sessionId = 0
            this.clearStorage();
        },

        async changePassword(payload: {
            current_password: string;
            new_password: string;
            new_password_confirmation: string;
        }) {
            await axios.post("/operator/v4/user/change-password", payload);
        },

    },
});
